// Analysis step of a recorded real conversation: score it against the standard
// of one of the learner's own scenarios (criteria + document protocol). The
// result also lands in Progress as a real conversation, next to practice.
import { useCallback, useEffect, useMemo, useState } from "react";
import { BarChart3, LoaderCircle, RotateCcw, Scale } from "lucide-react";
import { go } from "@/shared/dashboard/shell/navigate";
import { privateExerciseFetch } from "@/shared/lib/privateExerciseApi";
import type {
  PrivateExerciseAssessment,
  PrivateExerciseListItem,
} from "@/shared/lib/privateExercises";
import {
  recordingSpeakers,
  type RecordingSegment,
} from "@/shared/lib/recordingTranscript";

interface RecordingAssessment {
  id: string;
  exercise_title: string;
  protocol_name: string | null;
  learner_speaker: string;
  overall_score: number;
  assessment: PrivateExerciseAssessment;
  created_at: string;
}

const COPY = {
  en: {
    title: "Score against a standard",
    body: "Pick the scenario whose standard this conversation should meet, and which speaker is you. The result joins your Progress as a real conversation.",
    standard: "Standard",
    choose: "Choose a scenario…",
    fromDocs: "From your documents",
    other: "Other scenarios",
    speaker: "Which speaker is you?",
    lines: (count: number) => `${count} line${count === 1 ? "" : "s"}`,
    score: "Score conversation",
    scoring: "Scoring against the standard… about 20 seconds.",
    noScenarios:
      "You have no scenarios yet. Build them from your documents in Scenarios, then score real conversations against them.",
    openPersona: "Open Scenarios",
    noSpeakers:
      "This source has no speaker-separated transcript yet, so it cannot be scored.",
    latest: "Result",
    previous: "Earlier scores",
    you: "You",
    progress: "See Progress",
    loadError: "Scoring data could not be loaded.",
    retry: "Try again",
    error: "The conversation could not be scored.",
  },
  uk: {
    title: "Оцінити за стандартом",
    body: "Виберіть сценарій, стандарту якого має відповідати ця розмова, і котрий мовець — ви. Результат потрапить у «Прогрес» як реальна розмова.",
    standard: "Стандарт",
    choose: "Виберіть сценарій…",
    fromDocs: "З ваших документів",
    other: "Інші сценарії",
    speaker: "Котрий мовець — ви?",
    lines: (count: number) => `Реплік: ${count}`,
    score: "Оцінити розмову",
    scoring: "Оцінюю за стандартом… близько 20 секунд.",
    noScenarios:
      "У вас ще немає сценаріїв. Побудуйте їх з документів у «Сценаріях», а потім оцінюйте реальні розмови за ними.",
    openPersona: "Відкрити Сценарії",
    noSpeakers:
      "У цього джерела ще немає транскрипту з розділенням на мовців, тож розмову не можна оцінити.",
    latest: "Результат",
    previous: "Попередні оцінки",
    you: "Ви",
    progress: "Відкрити Прогрес",
    loadError: "Не вдалося завантажити дані для оцінки.",
    retry: "Спробувати ще",
    error: "Не вдалося оцінити розмову.",
  },
  pl: {
    title: "Oceń według standardu",
    body: "Wybierz scenariusz, którego standard ta rozmowa powinna spełnić, i który mówca to Ty. Wynik trafi do Postępów jako rzeczywista rozmowa.",
    standard: "Standard",
    choose: "Wybierz scenariusz…",
    fromDocs: "Z Twoich dokumentów",
    other: "Inne scenariusze",
    speaker: "Który mówca to Ty?",
    lines: (count: number) => `Wypowiedzi: ${count}`,
    score: "Oceń rozmowę",
    scoring: "Oceniam według standardu… około 20 sekund.",
    noScenarios:
      "Nie masz jeszcze scenariuszy. Zbuduj je z dokumentów w Scenariuszach, a potem oceniaj według nich rzeczywiste rozmowy.",
    openPersona: "Otwórz Scenariusze",
    noSpeakers:
      "To źródło nie ma jeszcze transkrypcji z podziałem na mówców, więc rozmowy nie da się ocenić.",
    latest: "Wynik",
    previous: "Wcześniejsze oceny",
    you: "Ty",
    progress: "Zobacz Postępy",
    loadError: "Nie udało się wczytać danych do oceny.",
    retry: "Spróbuj ponownie",
    error: "Nie udało się ocenić rozmowy.",
  },
  ro: {
    title: "Evaluează după un standard",
    body: "Alege scenariul al cărui standard trebuie să-l îndeplinească această conversație și care vorbitor ești tu. Rezultatul intră în Progres ca o conversație reală.",
    standard: "Standard",
    choose: "Alege un scenariu…",
    fromDocs: "Din documentele tale",
    other: "Alte scenarii",
    speaker: "Care vorbitor ești tu?",
    lines: (count: number) => `Replici: ${count}`,
    score: "Evaluează conversația",
    scoring: "Evaluez după standard… cam 20 de secunde.",
    noScenarios:
      "Nu ai încă scenarii. Construiește-le din documente în Scenarii, apoi evaluează conversațiile reale după ele.",
    openPersona: "Deschide Scenarii",
    noSpeakers:
      "Această sursă nu are încă o transcriere separată pe vorbitori, deci conversația nu poate fi evaluată.",
    latest: "Rezultat",
    previous: "Evaluări anterioare",
    you: "Tu",
    progress: "Vezi Progresul",
    loadError: "Datele pentru evaluare nu au putut fi încărcate.",
    retry: "Încearcă din nou",
    error: "Conversația nu a putut fi evaluată.",
  },
};

export function RecordingStandardPanel({
  sessionId,
  segments,
  locale,
}: {
  sessionId: string;
  segments: RecordingSegment[];
  locale: string;
}) {
  const copy = COPY[locale as keyof typeof COPY] ?? COPY.en;
  const speakers = useMemo(() => recordingSpeakers(segments), [segments]);
  const [scenarios, setScenarios] = useState<PrivateExerciseListItem[] | null>(
    null,
  );
  const [assessments, setAssessments] = useState<RecordingAssessment[]>([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const [exerciseId, setExerciseId] = useState("");
  const [learnerSpeaker, setLearnerSpeaker] = useState("");
  const [scoring, setScoring] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadFailed(false);
    try {
      const [list, scored] = await Promise.all([
        privateExerciseFetch<{ exercises: PrivateExerciseListItem[] }>(
          "/api/private-exercises",
        ),
        privateExerciseFetch<{ assessments: RecordingAssessment[] }>(
          `/api/recording-assessments?clientSessionId=${encodeURIComponent(sessionId)}`,
        ),
      ]);
      setScenarios(list.exercises);
      setAssessments(scored.assessments);
    } catch {
      setLoadFailed(true);
    }
  }, [sessionId]);
  useEffect(() => {
    void load();
  }, [load]);

  // Default to whoever the earlier score said was the learner, else the first voice.
  useEffect(() => {
    if (learnerSpeaker || speakers.length === 0) return;
    const previous = assessments[0]?.learner_speaker;
    setLearnerSpeaker(
      previous && speakers.some((speaker) => speaker.key === previous)
        ? previous
        : speakers[0].key,
    );
  }, [assessments, learnerSpeaker, speakers]);

  const grounded = (scenarios ?? []).filter((scenario) => scenario.protocol);
  const plain = (scenarios ?? []).filter((scenario) => !scenario.protocol);

  const score = async () => {
    if (!exerciseId || !learnerSpeaker || scoring) return;
    setScoring(true);
    setError(null);
    try {
      const { assessment } = await privateExerciseFetch<{
        assessment: RecordingAssessment;
      }>("/api/recording-assessments", {
        method: "POST",
        body: JSON.stringify({
          clientSessionId: sessionId,
          exerciseId,
          learnerSpeaker,
          locale,
        }),
      });
      setAssessments((current) => [assessment, ...current]);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : copy.error);
    } finally {
      setScoring(false);
    }
  };

  const latest = assessments[0];

  return (
    <section className="eq-session-surface eq-standard-panel">
      <h2>
        <Scale size={16} aria-hidden="true" />
        {copy.title}
      </h2>
      <p className="eq-standard-note">{copy.body}</p>

      {loadFailed ? (
        <div
          role="alert"
          className="eq-knowledge-alert eq-knowledge-alert--action"
        >
          <span>{copy.loadError}</span>
          <button
            type="button"
            className="eq-button"
            onClick={() => void load()}
          >
            <RotateCcw size={14} aria-hidden="true" />
            {copy.retry}
          </button>
        </div>
      ) : scenarios === null ? (
        <span className="eq-spinner" aria-hidden="true" />
      ) : speakers.length === 0 ? (
        <p className="eq-standard-note">{copy.noSpeakers}</p>
      ) : scenarios.length === 0 ? (
        <div className="eq-progress-empty-inline">
          <p>{copy.noScenarios}</p>
          <button
            type="button"
            className="eq-button"
            onClick={() => go("/dashboard/scenarios")}
          >
            {copy.openPersona}
          </button>
        </div>
      ) : (
        <div className="eq-standard-form">
          <label htmlFor="standard-scenario">{copy.standard}</label>
          <select
            id="standard-scenario"
            value={exerciseId}
            disabled={scoring}
            onChange={(event) => setExerciseId(event.target.value)}
          >
            <option value="">{copy.choose}</option>
            {grounded.length > 0 && (
              <optgroup label={copy.fromDocs}>
                {grounded.map((scenario) => (
                  <option key={scenario.id} value={scenario.id}>
                    {scenario.title} · {scenario.protocol!.name}
                  </option>
                ))}
              </optgroup>
            )}
            {plain.length > 0 && (
              <optgroup label={copy.other}>
                {plain.map((scenario) => (
                  <option key={scenario.id} value={scenario.id}>
                    {scenario.title}
                  </option>
                ))}
              </optgroup>
            )}
          </select>

          <fieldset disabled={scoring}>
            <legend>{copy.speaker}</legend>
            {speakers.map((speaker) => (
              <label key={speaker.key} className="eq-standard-speaker">
                <input
                  type="radio"
                  name="learner-speaker"
                  value={speaker.key}
                  checked={learnerSpeaker === speaker.key}
                  onChange={() => setLearnerSpeaker(speaker.key)}
                />
                <span>
                  <strong>
                    {speaker.key} · {copy.lines(speaker.lines)}
                  </strong>
                  <small>“{speaker.sample}”</small>
                </span>
              </label>
            ))}
          </fieldset>

          <div className="eq-standard-actions">
            {scoring && (
              <span className="eq-material-builder-note" aria-live="polite">
                {copy.scoring}
              </span>
            )}
            <button
              type="button"
              className="eq-button eq-button--primary"
              disabled={!exerciseId || !learnerSpeaker || scoring}
              onClick={() => void score()}
            >
              {scoring ? (
                <LoaderCircle
                  size={16}
                  className="motion-safe:animate-spin"
                  aria-hidden="true"
                />
              ) : (
                <Scale size={16} aria-hidden="true" />
              )}
              {copy.score}
            </button>
          </div>
          {error && (
            <p role="alert" className="eq-knowledge-alert">
              {error}
            </p>
          )}
        </div>
      )}

      {latest && (
        <div className="eq-standard-result" aria-live="polite">
          <header>
            <div>
              <span>{copy.latest}</span>
              <h3>
                {latest.exercise_title}
                {latest.protocol_name ? ` · ${latest.protocol_name}` : ""}
              </h3>
              <small>
                {copy.you}: {latest.learner_speaker} ·{" "}
                {new Date(latest.created_at).toLocaleDateString(locale)}
              </small>
            </div>
            <strong className="eq-standard-score">
              {latest.overall_score}
              <small>/100</small>
            </strong>
          </header>
          <p>{latest.assessment.summary}</p>
          <ul>
            {latest.assessment.criteria.map((criterion) => (
              <li key={criterion.criterionId}>
                <div>
                  <span>{criterion.label}</span>
                  <span className="eq-progress-score">
                    <span className="eq-progress-bar" aria-hidden="true">
                      <span
                        style={{ width: `${Math.max(2, criterion.score)}%` }}
                      />
                    </span>
                    {criterion.score}
                  </span>
                </div>
                <p>{criterion.feedback}</p>
              </li>
            ))}
          </ul>
          {assessments.length > 1 && (
            <details>
              <summary>{copy.previous}</summary>
              <ul className="eq-standard-previous">
                {assessments.slice(1).map((item) => (
                  <li key={item.id}>
                    <span>{item.exercise_title}</span>
                    <span>{item.overall_score}/100</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
          <button
            type="button"
            className="eq-button"
            onClick={() => go("/dashboard/progress")}
          >
            <BarChart3 size={15} aria-hidden="true" />
            {copy.progress}
          </button>
        </div>
      )}
    </section>
  );
}
